import { useState, useMemo, useRef } from "react"
import { agrogestao } from "@/api/agrogestaoClient"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { motion, AnimatePresence } from "framer-motion"
import { Plus, Edit2, Trash2, MapPin, Wheat, Coffee, Leaf, Sun } from "lucide-react"
import Paginacao from "../components/shared/Paginacao"

export default function Talhoes() {
  const MAX_NAME_LEN = 100
  const MAX_CULTURA_LEN = 100
  const normalize = (s) => s ? s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_') : ''
  const CULTURE_OPTIONS = [
    { key: 'soja', label: 'Soja', Icon: Wheat },
    { key: 'milho', label: 'Milho', Icon: Wheat },
    { key: 'milho_safrinha', label: 'Milho Safrinha', Icon: Wheat },
    { key: 'algodao', label: 'Algodão', Icon: Leaf },
    { key: 'cafe', label: 'Café', Icon: Coffee },
    { key: 'cana_de_acucar', label: 'Cana-de-açúcar', Icon: Leaf },
    { key: 'feijao', label: 'Feijão', Icon: Leaf },
    { key: 'arroz', label: 'Arroz', Icon: Wheat },
    { key: 'trigo', label: 'Trigo', Icon: Wheat },
    { key: 'sorgo', label: 'Sorgo', Icon: Wheat },
    { key: 'girassol', label: 'Girassol', Icon: Sun },
    { key: 'amendoim', label: 'Amendoim', Icon: Leaf },
    { key: 'outro', label: 'Outro', Icon: Leaf },
  ]
  const getCulturaIcon = (s) => {
    const k = normalize(s)
    const opt = CULTURE_OPTIONS.find(o => o.key === k)
    return (opt?.Icon) || Wheat
  }

  const propriedadeId = localStorage.getItem("propriedade_selecionada")
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [formData, setFormData] = useState({ nome: "", area_hectares: "", cultura_atual: "", status: "preparando" })
  const [errorMsg, setErrorMsg] = useState("")
  const [saving, setSaving] = useState(false)
  const timeoutRef = useRef(null)
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('todos')
  const [filterCultura, setFilterCultura] = useState('todas')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(12)
  const normalizeStatus = (s) => s ? s.toLowerCase().replace(/\s+/g, '_') : null
  const STATUS_OPTIONS = [
    { value: 'preparando', label: 'Preparando' },
    { value: 'plantado', label: 'Plantado' },
    { value: 'em_desenvolvimento', label: 'Em desenvolvimento' },
    { value: 'colheita', label: 'Colheita' },
    { value: 'pousio', label: 'Pousio' }
  ]
  const formatStatus = (s) => {
    const v = s ? s.toLowerCase() : ''
    if (v === 'preparando') return 'Preparando'
    if (v === 'plantado') return 'Plantado'
    if (v === 'em_desenvolvimento') return 'Em desenvolvimento'
    if (v === 'colheita') return 'Colheita'
    if (v === 'pousio') return 'Pousio'
    return s || '-'
  }

  const { data: talhoes = [], isLoading } = useQuery({
    queryKey: ["talhoes", propriedadeId],
    queryFn: () => propriedadeId 
      ? agrogestao.entities.Talhao.filter({ propriedade_id: propriedadeId }, "-id")
      : Promise.resolve([]),
    enabled: !!propriedadeId
  })

  const talhoesFiltrados = useMemo(() => {
    return talhoes.filter(t => {
      if (filterStatus !== 'todos' && normalizeStatus(t.status) !== filterStatus) return false
      if (filterCultura !== 'todas' && normalize(t.cultura_atual) !== filterCultura) return false
      if (searchTerm) {
        const s = searchTerm.toLowerCase()
        const n = (t.nome || '').toLowerCase()
        const c = (t.cultura_atual || '').toLowerCase()
        if (!n.includes(s) && !c.includes(s)) return false
      }
      return true
    })
  }, [talhoes, filterStatus, filterCultura, searchTerm])

  const start = (currentPage - 1) * pageSize
  const pageItems = useMemo(() => talhoesFiltrados.slice(start, start + pageSize), [talhoesFiltrados, start, pageSize])

  const totalTalhoes = talhoes.length
  const areaTotal = useMemo(() => talhoes.reduce((acc, t) => acc + (typeof t.area_hectares === 'number' ? t.area_hectares : 0), 0), [talhoes])
  const areaMedia = useMemo(() => totalTalhoes ? areaTotal / totalTalhoes : 0, [areaTotal, totalTalhoes])

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const u = await agrogestao.auth.me()
      const payload = {
        nome: (data.nome || "").trim().slice(0, MAX_NAME_LEN),
        propriedade_id: propriedadeId,
        area_hectares: data.area_hectares ? parseFloat(data.area_hectares) : null,
        cultura_atual: (data.cultura_atual || "").trim().slice(0, MAX_CULTURA_LEN) || null,
        status: normalizeStatus(data.status) || null,
        created_by: u?.email || null
      }
      // Timeout de 30s
      const op = agrogestao.entities.Talhao.create(payload)
      const timeoutPromise = new Promise((_, reject) => {
        timeoutRef.current = setTimeout(() => reject(new Error("timeout")), 30000)
      })
      return Promise.race([op, timeoutPromise])
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["talhoes", propriedadeId] })
      resetForm()
      setSaving(false)
    },
    onError: (err) => {
      let m = err?.message || (err?.error?.message) || "Erro ao salvar talhão"
      if (m === "timeout") {
        m = "Tempo esgotado ao salvar. Verifique a conexão e tente novamente."
      } else if (err?.code === "23505") {
        m = "Já existe um talhão com este nome nesta propriedade."
      } else if (String(m).toLowerCase().includes("fetch") || String(m).toLowerCase().includes("network")) {
        m = "Falha na comunicação com o servidor. Tente novamente."
      }
      setErrorMsg(m)
      setSaving(false)
    }
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => {
      const op = agrogestao.entities.Talhao.update(id, {
        nome: (data.nome || "").trim().slice(0, MAX_NAME_LEN),
        area_hectares: data.area_hectares ? parseFloat(data.area_hectares) : null,
        cultura_atual: (data.cultura_atual || "").trim().slice(0, MAX_CULTURA_LEN) || null,
        status: normalizeStatus(data.status) || null
      })
      const timeoutPromise = new Promise((_, reject) => {
        timeoutRef.current = setTimeout(() => reject(new Error("timeout")), 30000)
      })
      return Promise.race([op, timeoutPromise])
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["talhoes", propriedadeId] })
      resetForm()
      setSaving(false)
    },
    onError: (err) => {
      let m = err?.message || (err?.error?.message) || "Erro ao atualizar talhão"
      if (m === "timeout") {
        m = "Tempo esgotado ao atualizar. Verifique a conexão e tente novamente."
      } else if (err?.code === "23505") {
        m = "Já existe um talhão com este nome nesta propriedade."
      } else if (String(m).toLowerCase().includes("fetch") || String(m).toLowerCase().includes("network")) {
        m = "Falha na comunicação com o servidor. Tente novamente."
      }
      setErrorMsg(m)
      setSaving(false)
    }
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => agrogestao.entities.Talhao.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["talhoes", propriedadeId] })
    }
  })

  const resetForm = () => {
    setFormData({ nome: "", area_hectares: "", cultura_atual: "", status: "preparando" })
    setEditingId(null)
    setDialogOpen(false)
    setErrorMsg("")
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const nome = (formData.nome || "").trim()
    if (!nome) {
      setErrorMsg("Informe o nome do talhão.")
      return
    }
    if (nome.length > MAX_NAME_LEN) {
      setErrorMsg(`Nome deve ter no máximo ${MAX_NAME_LEN} caracteres.`)
      return
    }
    const area = formData.area_hectares ? parseFloat(formData.area_hectares) : null
    if (!area || isNaN(area) || area <= 0) {
      setErrorMsg("Informe a área em hectares.")
      return
    }
    const cultura = (formData.cultura_atual || "").trim()
    if (!cultura) {
      setErrorMsg("Informe a cultura atual.")
      return
    }
    if ((formData.cultura_atual || "").trim().length > MAX_CULTURA_LEN) {
      setErrorMsg(`Cultura atual deve ter no máximo ${MAX_CULTURA_LEN} caracteres.`)
      return
    }
    setErrorMsg("")
    setSaving(true)
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: formData }, {
        onSettled: () => { if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null } }
      })
    } else {
      createMutation.mutate(formData, {
        onSettled: () => { if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null } }
      })
    }
  }

  if (!propriedadeId) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Selecione uma propriedade</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-stone-700 mb-4">Para cadastrar talhões, selecione primeiro a propriedade em Propriedades.</p>
            <a href="/Propriedades" className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg">Ir para Propriedades</a>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <MapPin className="w-8 h-8 text-[#40916C] animate-bounce" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-800">Talhões</h1>
          <p className="text-stone-600 mt-1">Cadastre e organize os talhões da propriedade selecionada</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="space-y-1">
            <Label>Status</Label>
            <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setCurrentPage(1) }}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Cultura</Label>
            <Select value={filterCultura} onValueChange={(v) => { setFilterCultura(v); setCurrentPage(1) }}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                {CULTURE_OPTIONS.map(opt => (
                  <SelectItem key={opt.key} value={opt.key}>
                    <opt.Icon className="w-4 h-4 mr-2 inline-block" />
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Buscar</Label>
            <Input placeholder="Nome ou cultura" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1) }} className="w-56" />
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => resetForm()} className="bg-[#1B4332] hover:bg-[#2D6A4F] gap-2">
              <Plus className="w-5 h-5" />
              Novo Talhão
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingId ? "Editar Talhão" : "Novo Talhão"}</DialogTitle>
              <DialogDescription>Informe o nome do talhão para cadastro</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input 
                  value={formData.nome} 
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value.slice(0, MAX_NAME_LEN) })} 
                  maxLength={MAX_NAME_LEN}
                  required 
                />
                <div className="text-xs text-stone-500">{(formData.nome || "").length}/{MAX_NAME_LEN}</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Área (ha) *</Label>
                  <Input type="number" step="0.01" value={formData.area_hectares} onChange={(e) => setFormData({ ...formData, area_hectares: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Cultura *</Label>
                  <Select value={formData.cultura_atual} onValueChange={(v) => setFormData({ ...formData, cultura_atual: v })}>
                    <SelectTrigger><SelectValue placeholder="Selecione a cultura" /></SelectTrigger>
                    <SelectContent>
                      {CULTURE_OPTIONS.map(opt => (
                        <SelectItem key={opt.key} value={opt.label}>
                          <opt.Icon className="w-4 h-4 mr-2 inline-block" />
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
                <Button type="submit" className="bg-[#40916C] hover:bg-[#2D6A4F] disabled:opacity-60 disabled:cursor-not-allowed" disabled={saving}>
                  {saving ? (
                    <span className="inline-flex items-center gap-2">
                      <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" className="opacity-25" />
                        <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" className="opacity-75" />
                      </svg>
                      Salvando...
                    </span>
                  ) : "Salvar"}
                </Button>
              </div>
              {errorMsg ? (
                <div className="text-red-600 text-sm pt-2">{errorMsg}</div>
              ) : null}
            </form>
          </DialogContent>
        </Dialog>
      </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-[#D4A373] to-[#BC8A5F] text-white shadow-lg">
          <CardContent className="p-4">
            <p className="text-xs/5 opacity-80">Talhões</p>
            <p className="text-2xl font-bold">{totalTalhoes}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-[#40916C] to-[#2D6A4F] text-white shadow-lg">
          <CardContent className="p-4">
            <p className="text-xs/5 opacity-80">Área Total (ha)</p>
            <p className="text-2xl font-bold">{areaTotal.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-lg">
          <CardContent className="p-4">
            <p className="text-xs/5 opacity-80">Área Média (ha)</p>
            <p className="text-2xl font-bold">{areaMedia.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-700 text-white shadow-lg">
          <CardContent className="p-4">
            <p className="text-xs/5 opacity-80">Em Desenvolvimento</p>
            <p className="text-2xl font-bold">{talhoes.filter(t => normalizeStatus(t.status) === 'em_desenvolvimento').length}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-4">
          {talhoesFiltrados.length === 0 ? (
            <div className="text-stone-600">Nenhum talhão cadastrado.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <AnimatePresence>
                {pageItems.map((t) => (
                  <motion.div key={t.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="border rounded-xl p-4 bg-white border-l-4 border-[#40916C]">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold text-stone-800">{t.nome || t.id}</div>
                      <Badge variant="secondary" className="bg-[#D4A373]/15 text-[#D4A373] border-[#D4A373]/30">{formatStatus(t.status)}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="p-2 bg-stone-50 rounded">
                        <p className="text-stone-500 text-xs">Área (ha)</p>
                        <p className="font-semibold text-stone-800">{typeof t.area_hectares === 'number' ? t.area_hectares : (t.area_hectares || '-')}</p>
                      </div>
                      <div className="p-2 bg-stone-50 rounded">
                        <p className="text-stone-500 text-xs">Cultura</p>
                        <p className="font-semibold text-stone-800 flex items-center gap-2">
                          {t.cultura_atual ? (
                            (() => {
                              const Icon = getCulturaIcon(t.cultura_atual)
                              return <Icon className="w-4 h-4 text-[#40916C]" />
                            })()
                          ) : null}
                          <span>{t.cultura_atual || '-'}</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-3">
                      <Button variant="ghost" size="sm" onClick={() => { setEditingId(t.id); setFormData({ nome: t.nome || "", area_hectares: t.area_hectares ?? "", cultura_atual: t.cultura_atual ?? "", status: normalizeStatus(t.status) || "preparando" }); setDialogOpen(true) }} className="text-stone-400 hover:text-stone-600">
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(t.id)} className="text-stone-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </CardContent>
      </Card>

      {talhoesFiltrados.length > pageSize && (
        <Paginacao
          totalItems={talhoesFiltrados.length}
          currentPage={currentPage}
          onPageChange={(p) => setCurrentPage(p)}
          pageSize={pageSize}
          onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1) }}
        />
      )}
    </div>
  )
}
